import axios from 'helpers/api';

/**
 * Centralized fetchers for SharedLiving Phase 11 endpoints. Keeping these out
 * of the components so call sites stay focused on UI concerns and so the
 * endpoint paths / response unwrapping live in one place.
 *
 * All fetchers throw on HTTP error so the caller can show inline
 * validation messages (UI-067) via try/catch.
 */

const baseUrl = () => process.env.REACT_APP_SERVER_URL;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('accessToken')}`
});

/**
 * GET /facilities/list-sharedliving-facilities/:groupId?
 *
 * Pharmacy-admin pass-through path param; non-pharmacy users always get their
 * own group regardless of the param (backend enforces). Returns the facilities
 * with `is_sharedliving_facility = 1` in the group.
 *
 * Used by UI-065 (caregiver registration facility picker).
 */
export const fetchSharedLivingFacilities = async (groupId) => {
  const path = groupId
    ? `/facilities/list-sharedliving-facilities/${groupId}`
    : '/facilities/list-sharedliving-facilities';
  const { data } = await axios.get(`${baseUrl()}${path}`, { headers: authHeaders() });
  return {
    status: !!data?.status,
    message: data?.message || '',
    data: Array.isArray(data?.data) ? data.data : []
  };
};

/**
 * GET /users/list-sharedliving-staff/:groupId?
 *
 * Returns role-5 staff in the group who have user_facility access to at least
 * one SL-flagged facility. Pharmacy roles can pass any group via path; role 4
 * locked to own group; role 5+ blocked.
 *
 * Used by UI-068 (nurse / case-manager dropdowns).
 */
export const fetchSharedLivingStaff = async (groupId) => {
  const path = groupId
    ? `/users/list-sharedliving-staff/${groupId}`
    : '/users/list-sharedliving-staff';
  const { data } = await axios.get(`${baseUrl()}${path}`, { headers: authHeaders() });
  return {
    status: !!data?.status,
    message: data?.message || '',
    data: Array.isArray(data?.data) ? data.data : []
  };
};

/**
 * GET /users/:userId/dependent-assignments
 *
 * Fetches the dependent assignments for a given user (role 5).
 * Used during deactivation to see if replacements are needed.
 */
export const fetchUserDependentAssignments = async (userId) => {
  const { data } = await axios.get(`${baseUrl()}/users/${userId}/dependent-assignments`, { headers: authHeaders() });
  return data;
};
